import BigNumber from "bignumber.js";
import type { BreadcrumbList, FAQPage, ItemList, Product as ProductLd, WithContext } from 'schema-dts';
import type { CategorySlug } from "~/dto/category";
import type { ProductFaqEntry } from "~/dto/faq-entry";
import type { Product } from "~/dto/product";

const unitCodeTable: { [key: string]: string } = {
  'kg': 'KGM',
  'KG': 'KGM',
  'L': 'LTR',
  'litre(s)': 'LTR',
  'm': 'MTR',
  'ML': 'MTR',
  'm²': 'MTK',
  'm2': 'MTK',
  'm3': 'MTQ',
  'ml': 'MLT',
};

export default defineNuxtPlugin({
  parallel: false, // Depends on 'images-src'
  setup: () => {
    const { $config, $router, $imageSrc } = useNuxtApp();
    const img = useImage();

    function jsonLdBreadcrumbs(items: CategorySlug[]): WithContext<BreadcrumbList> | null {
      if (!items) {
        return null;
      }

      let i = 1;
      const jsonLdItem: WithContext<BreadcrumbList> = {
        "@context": "https://schema.org",
        "@type": 'BreadcrumbList',
        itemListElement: items?.map(item => ({
          "@type": "ListItem",
          position: i++,
          name: item.name,
          item: `${$config.public.selfUrl}${$router?.resolve(categorySlugPath(item)).href}`,
        })),
      };

      return jsonLdItem;
    }

    function jsonLdCategory(category: CategorySlug, products: Product[]): WithContext<ItemList> | null {
      if (!category || !products) {
        return null;
      }

      let position = 0;

      const itemList: WithContext<ItemList> = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: category.name,
        url: `${$config.public.selfUrl}${$router?.resolve(categorySlugPath(category)).href}?page=${$router.currentRoute.value.query.page ?? 1}`,
        numberOfItems: products.length,
        itemListElement: products?.map(item => ({
          "@type": "ListItem",
          position: position++,
          item: {
            "@type": "Thing",
            name: item.name,
            url: `${$config.public.selfUrl}${$router.resolve(productPathByRef(item)).href}`,
            description: item.meta_description ?? undefined,
            image: item.image ? img($imageSrc(item.image)!, { width: 300, format: 'webp' }) : undefined,
          },
        })) ?? [],
      };
      return itemList;
    }

    function jsonLdProduct(item: Product): WithContext<ProductLd> | null {
      if (!item) {
        return null;
      }

      // Test URL: https://search.google.com/test/rich-results
      const unitCode = item.packaging_unit ? unitCodeTable[item.packaging_unit] : undefined;
      const jsonLdItem: WithContext<ProductLd> = {
        "@context": "https://schema.org",
        "@type": "Product",
        image: item.image ? img($imageSrc(item.image)!, { width: 300, format: 'webp' }) : undefined,
        name: item.name,
        sku: item.reference_slug ?? item.id,
        brand: item.brand_name ? { "@type": "Brand", name: item.brand_name } : undefined,
        category: item.category_name ? { "@type": "Thing", name: item.category_name, url: `${$config.public.selfUrl}${$router?.resolve(categorySlugPath({ slug: item.category_slug } as CategorySlug)).href}` } : undefined,
        description: item.meta_description ?? undefined,
        gtin: item.gtin ?? undefined,
        offers: item.price_state === 'NC' ? undefined : {
          "@type": "Offer",
          itemCondition: "https://schema.org/NewCondition",
          availability: item.availability === 'RT' ? "https://schema.org/BackOrder" : item.availability === 'RD' ? "https://schema.org/Discontinued" : "https://schema.org/InStock",
          seller: { "@type": "Organization", name: "Prix-de-gros.com" },
          url: `${$config.public.selfUrl}${$router?.resolve(productPathByRef(item)).href}`,
          // canonical_link: `${$config.selfUrl}${app.router?.resolve($productPathByRef(item)).href}`,
          // priceCurrency: "EUR",
          // sale_price: item.price_sell ? BigNumber(item.price_sell).plus(item.eco_part ?? 0).multipliedBy(1 + $config.vatRate).toFixed(2) : undefined,
          // price: BigNumber(item.price_sell).plus(item.eco_part ?? 0).multipliedBy(1 + $config.vatRate).toFixed(2),
          // price: item.price_public ? (Math.round(100 * item.price_public * (1 + $config.vatRate)) / 100).toFixed(2) : (Math.round(100 * item.price_sell * (1 + $config.vatRate)) / 100).toFixed(2), // TODO: BigNumber
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: new BigNumber(item.price_sell).plus(item.eco_part ?? 0).multipliedBy(1 + $config.public.vatRate).toFixed(2),
            priceCurrency: "EUR",
            referenceQuantity: unitCode !== undefined
              ? {
                "@type": "QuantitativeValue",
                value: item.packaging_quantity,
                unitCode: unitCode,
                unitText: item.packaging_unit,
                // valueReference: {
                //   "@type": "QuantitativeValue",
                //   value: "750",
                //   unitCode: "MLT"
                // }
              }
              : undefined,
          },
        },
        aggregateRating: item.vote && item.vote_count ? { "@type": "AggregateRating", ratingValue: item.vote, reviewCount: item.vote_count } : undefined,
      };

      return jsonLdItem;
    }

    function jsonLdFAQPage(items: ProductFaqEntry[]): WithContext<FAQPage> | null {
      if (!items || items.length === 0) {
        return null;
      }

      const jsonLdItem: WithContext<FAQPage> = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items?.map(item => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer
          }
        }))
      };

      return jsonLdItem;
    }

    return {
      provide: {
        jsonLdBreadcrumbs,
        jsonLdProduct,
        jsonLdCategory,
        jsonLdFAQPage,
      }
    };
  },
});
